import {
    useQuery,
    type QueryKey,
    type UseQueryOptions,
    type UseQueryResult,
} from '@tanstack/react-query'
import { AxiosResponse } from 'axios'

import { atlasClient, AtlasUserOverride } from 'api/atlasClient'
import { CerebroPaginatedResponse, PaginatedResponse } from 'api/Cerebro/types'

export type Operator =
    | '_in'
    | '_eq'
    | '_neq'
    | '_gt'
    | '_gte'
    | '_lt'
    | '_lte'
    | '_is_null'
    | '_not_null'
    | '_not_in'
    | '_contains'
    | '_not_contains'
    | '_starts_with'
    | '_between'
    | '_text_matches'

export interface FieldPredicate {
    field: string
    operator: Operator
    value: unknown
}

export type BooleanExpression = { _and: Expression[] } | { _or: Expression[] }

export type Expression = FieldPredicate | BooleanExpression

export type HulkQueryFilterConfig = Expression

export type HulkQueryTimeComparison =
    | { mode: 'explicit'; start: string; end: string }
    | { mode: 'prior_period' }
    | { mode: 'shift'; unit: 'year'; amount: number }

export interface HulkQueryTimeConfig {
    dimension?: string
    start?: string
    end?: string
    comparison?: HulkQueryTimeComparison
    align_to_grain?: boolean
}

export interface HulkQuerySortExpression<Fields extends string = string> {
    field: Fields
    direction: 'asc' | 'desc'
}

export interface HulkQueryPaginationConfig {
    limit?: number
    offset?: number
    include_count?: boolean
}

export interface HulkQueryTopNConfig<
    DimensionName extends string = string,
    MetricName extends string = string,
> {
    n: number
    dimensions: DimensionName[]
    ordering: HulkQuerySortExpression<DimensionName | MetricName>[]
    partition_by?: DimensionName[]
    include_others?: boolean
}

export interface HulkQueryFormattingRule<MetricName extends string = string> {
    fields: MetricName[]
    rounding?: number
}

export interface HulkQueryFormattingConfig<
    DimensionName extends string = string,
    MetricName extends string = string,
> {
    rules?: HulkQueryFormattingRule<MetricName>[]
    field_labels?: { [field in DimensionName | MetricName]?: string }
    field_order?: (DimensionName | MetricName)[]
}

interface FixedWidthBinning {
    strategy: 'fixed_width'
    width: number
    min_value?: number | null
    max_value?: number | null
}

interface QuantileBinning {
    strategy: 'quantile'
    type: 'quartile' | 'quintile' | 'decile'
    include_bounds?: boolean
}

interface Bin {
    min: number
    max: number
    label?: string | null
}

interface CustomBinning {
    strategy: 'custom'
    bins: Bin[]
    sort_order?: string[]
    below_min_label?: string
    above_max_label?: string
}

interface CategoricalBinning {
    strategy: 'categorical'
    mapping: { [category: string]: string }
    sort_order?: string[]
    unmapped_handling?: 'other' | 'pass_through'
}

export type BinningConfig =
    | FixedWidthBinning
    | QuantileBinning
    | CustomBinning
    | CategoricalBinning

export interface DimensionWithBinning<
    BinnableDimensionName extends string = string,
> {
    name: string
    source: BinnableDimensionName
    binning: BinningConfig
}

export interface HulkQuery<
    DimensionName extends string = string,
    MetricName extends string = string,
    BinnableDimensionName extends string = string,
    EnrichmentName extends string = string,
> {
    dimensions: (DimensionName | DimensionWithBinning<BinnableDimensionName>)[]
    metrics: MetricName[]
    filters?: HulkQueryFilterConfig
    time?: HulkQueryTimeConfig
    ordering?: HulkQuerySortExpression<DimensionName | MetricName>[]
    pagination?: HulkQueryPaginationConfig
    top_n?: HulkQueryTopNConfig<DimensionName, MetricName>
    formatting?: HulkQueryFormattingConfig<DimensionName | MetricName>
    enrichments?: EnrichmentName[]
}

export interface HulkQueryResponseTimeRangeMeta {
    start: string
    end: string
    comparison_start: string | null
    comparison_end?: string | null
}

interface HulkQueryResponseMeta {
    total: number | null
    limit: number | null
    offset: number | null
    time_range: HulkQueryResponseTimeRangeMeta | null
}

export type HulkEnrichmentLookup = Record<
    string,
    Record<string, Record<string, unknown>>
>

export interface HulkQueryResponse<T = { [key: string]: unknown }> {
    data: T[]
    meta: HulkQueryResponseMeta
    enrichments?: HulkEnrichmentLookup | null
}

export function queryHulkDatasource<
    T = { [key: string]: unknown },
    Q extends HulkQuery = HulkQuery,
>(
    datasource: string,
    query: Q,
    override?: AtlasUserOverride
): Promise<AxiosResponse<HulkQueryResponse<T>>> {
    return atlasClient.post<HulkQueryResponse<T>>(
        `/api/v1/hulk/datasources/${datasource}/query`,
        query,
        override ? { userOverride: override } : undefined
    )
}

/**
 * A Cerebro-shaped envelope that also carries the window hulk resolved.
 *
 * hulk widens a requested range to whole units of the datasource's grain and
 * reports what it actually queried on `meta.time_range`; a widget can only state
 * the window its numbers cover if that survives the translation. `time_range` is
 * optional, so this stays mutually assignable with `CerebroPaginatedResponse` —
 * and it lives here rather than on that shared type, which every Cerebro
 * resource uses and shouldn't grow a field only hulk populates.
 */
export type HulkPaginatedEnvelope<T> = Omit<
    CerebroPaginatedResponse<T>,
    'data'
> & {
    data: PaginatedResponse<T> & {
        time_range?: HulkQueryResponseTimeRangeMeta | null
    }
}

export function toCerebroPaginatedEnvelope<T>(
    response: AxiosResponse<HulkQueryResponse<T>>
): HulkPaginatedEnvelope<T & { results_count: number }> {
    const { data: hulkData, ...axiosRest } = response
    const count = hulkData.meta?.total ?? hulkData.data.length
    // Banner-shaped records carry `results_count` per row, which the table's
    // totals row reads to render "Total: N Rows" (BodyCell, TotalRowsCountCell).
    // Hulk reports the same number in `meta.total` instead — project it onto
    // each record so the existing table machinery picks it up.
    const results = hulkData.data.map((record) => ({
        ...record,
        results_count: count,
    }))
    return {
        ...axiosRest,
        data: {
            count,
            next: null,
            previous: null,
            results,
            time_range: hulkData.meta?.time_range ?? null,
        },
    }
}

interface UseHulkQueryOptions<
    T = { [key: string]: unknown },
    Q extends HulkQuery = HulkQuery,
> {
    datasource: string
    query: Q
    queryKey: QueryKey
    enabled?: boolean
    select?: UseQueryOptions<
        HulkQueryResponse<T>,
        Error,
        HulkQueryResponse<T>,
        QueryKey
    >['select']
    placeholderData?: UseQueryOptions<
        HulkQueryResponse<T>,
        Error,
        HulkQueryResponse<T>,
        QueryKey
    >['placeholderData']
}

export function useHulkQuery<
    T = { [key: string]: unknown },
    Q extends HulkQuery = HulkQuery,
>({
    datasource,
    query,
    queryKey,
    enabled,
    select,
    placeholderData,
}: UseHulkQueryOptions<T, Q>): UseQueryResult<HulkQueryResponse<T>, Error> {
    return useQuery<
        HulkQueryResponse<T>,
        Error,
        HulkQueryResponse<T>,
        QueryKey
    >({
        queryKey,
        queryFn: async () => {
            const response = await queryHulkDatasource<T, Q>(datasource, query)
            return response.data
        },
        enabled,
        select,
        placeholderData,
    })
}
